import * as React from 'react';
import { Index } from 'react-virtualized';
import { RenderedRows } from 'react-virtualized/dist/es/List';
import styled from 'styled-components';
import { LIST_ROW_HEIGHTS } from '../../../../constants/list_row_heights';
import { IAthlete, IListRowRenderProps, ITeam } from '../../../../data/types';
import { Row } from '../../../layout/Row';
import { AthleteMerger } from '../../../shared/form/AthleteMerger';
import { SelectBox } from '../../../shared/form/SelectBox';
import { Icon } from '../../../shared/Icon';
import { List, ListSectionHeading } from '../../../shared/List/List';
import { ListAvatar } from '../../../shared/List/ListAvatar';
import { ListItem, ListItemCell } from '../../../shared/List/ListItem';
import {
  groupByParentEntity,
  IGroupedEntityListItem,
} from '../../../shared/List/listUtils';
import { NoResults } from '../../../shared/NoResults';

interface IProps {
  athletes: IAthlete[];
  teams: ITeam[];
  selectedAthletes: IAthlete[];
  selectedTeams: ITeam[];
  onSelect: (athlete: IAthlete, checked: boolean) => void;
  clearSelected: () => void;
  disabled: boolean;
  emptyMessage?: string;
  showMergeOption?: boolean;
  /** When true, renders a flat list without team grouping headers */
  flat?: boolean;
}

export const MultiAthleteSelectList = (props: IProps): React.ReactElement => {
  const [heading, setHeading] = React.useState<string>();
  /**
   * @description render function for list items
   * @param param0
   */
  const rowRenderer = ({
    index,
    key,
    style,
  }: IListRowRenderProps): React.ReactElement => {
    const row = data[index];
    if (row.isSticky) {
      return (
        <ListSectionHeading key={key} style={style} label={row.sectionLabel} />
      );
    } else {
      return (
        <AthleteListItem
          showMergeOption={props.showMergeOption}
          style={style}
          athlete={row.data}
          key={key}
          onSelect={props.onSelect}
          selected={props.selectedAthletes?.includes(row.data)}
        />
      );
    }
  };

  /**
   * @description creates a memoized list of items grouped by team that updates when props update
   */
  const data = React.useMemo((): IGroupedEntityListItem[] => {
    // If flat mode, return simple list without grouping
    if (props.flat) {
      return props.athletes.map((athlete: IAthlete): IGroupedEntityListItem => ({
        isSticky: false,
        sectionLabel: '',
        data: athlete,
      }));
    }
    /// only group by currently selected teams
    const activeParentIds = props.selectedTeams.map((t: ITeam): string => t.id);
    const activeParents = props.teams.filter((t: ITeam): boolean =>
      activeParentIds.includes(t.id)
    );
    return groupByParentEntity(activeParents, props.athletes, 'teams');
  }, [props.teams, props.athletes?.length, props.selectedAthletes?.length, props.flat]);

  const onRowsRendered = ({ startIndex }: RenderedRows): void => {
    if (data.length) {
      setHeading(data[startIndex].sectionLabel);
    }
  };

  const computeGroupedListRowHeight = (info: Index): number =>
    data[info.index]?.isSticky
      ? LIST_ROW_HEIGHTS.stickyHeader
      : LIST_ROW_HEIGHTS.normal;

  return props.disabled ? (
    <DisabledMessage>
      <Icon name='error_outline' size='medium' />
      <h6>Select a team to add athletes to this group</h6>
    </DisabledMessage>
  ) : data.length ? (
    <List
      rowHeight={props.flat ? LIST_ROW_HEIGHTS.normal : computeGroupedListRowHeight}
      rowRenderer={rowRenderer}
      listLength={data.length}
      onRowsRendered={props.flat ? undefined : onRowsRendered}
      stickyHeading={props.flat ? undefined : heading}
      roundedContainer
    />
  ) : (
    <NoResults
      message={
        props.emptyMessage
          ? props.emptyMessage
          : 'No athletes exist in the selected team(s)'
      }
    />
  );
};

interface IListItemProps {
  athlete: IAthlete;
  onSelect: (athlete: IAthlete, checked: boolean) => void;
  style: React.CSSProperties;
  selected: boolean;
  showMergeOption?: boolean;
}

const AthleteListItem = (props: IListItemProps): React.ReactElement => {
  const selectionHandler = (
    e: React.SyntheticEvent<HTMLInputElement>
  ): void => {
    const { checked } = e.currentTarget;
    props.onSelect(props.athlete, checked);
  };
  return (
    <ListItem style={props.style}>
      <ListItemCell>
        <Row>
          <ListAvatar entity={props.athlete} />
        </Row>
      </ListItemCell>
      <ListItemCell>
        {props.showMergeOption && (
          <AthleteMerger athlete={props.athlete} level={1} iconButton />
        )}
        <SelectBox selected={props.selected} onSelect={selectionHandler} />
      </ListItemCell>
    </ListItem>
  );
};

const DisabledMessage = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  h6 {
    width: 50%;
  }
`;
